import React, { useCallback } from 'react'
import dayjs from 'dayjs'
import { Client } from '../../../../pure-js/types/GrooverTypes'
import type { WalkInReceiptItem } from '../../../../pure-js/libs/getWalkInReceiptItems'
import { addSsnForEnrollmentUser } from '../../libs/CloudFunctionsApiHandler'
import { usePortalAuth } from '../../hooks/usePortalAuth'
import { downloadReceiptPdf } from '../../libs/downloadReceiptPdf'
import { useReceiptSsn } from '../../hooks/useReceiptSsn'
import { ReceiptSsnInputForm } from '../../components/ReceiptSsnInputForm'
import GrooverText from '../../components/GrooverText'
import { formatGrooverText } from '../../libs/GrooverTextRepository'
import {
  DetailCard,
  DetailCardActionsRow,
  DetailCardCloseButton,
  DetailCardCloseFooter,
  DetailCardContent,
  DetailCardDownloadButton,
  DetailCardReceiptBox,
  DetailCardReceiptRef,
  DetailCardReceiptRows,
  DetailCardRefundNote,
  DetailCardRow,
  DetailCardRowLabel,
  DetailCardRowValue,
  DetailCardSellerBlock,
  DetailCardSellerLabel,
  DetailCardSellerValue,
  DetailCardSsnInputRow,
  DetailCardSsnToggle,
  DetailCardTitle,
  DetailCardTopRow,
  DetailOverlay
} from './PortalWalkinTicketsPage.styles'

const RECEIPT_ELEMENT_ID = 'walkin-receipt-printable'

const formatPrice = (cents: number, currency: string): string => `${(cents / 100).toFixed(2)} ${currency}`

interface PortalWalkinReceiptCardProps {
  item: WalkInReceiptItem
  client: Client | null
  enrollmentUserId: string
  getText: (key: string) => string
  onClose: () => void
}

export const PortalWalkinReceiptCard: React.FC<PortalWalkinReceiptCardProps> = ({
  item,
  client,
  enrollmentUserId,
  getText,
  onClose
}) => {
  const { isViewAs } = usePortalAuth()
  const { enrollment, payment, activity, quantity, refundedCents } = item
  const currency = (payment.currency as string) || 'SEK'

  const vatRate: number | undefined =
    activity.vat !== undefined ? activity.vat : client?.defaultVAT !== undefined ? client.defaultVAT : undefined
  const vatMultiplier = vatRate !== undefined && vatRate > 0 ? 1 + vatRate / 100 : 1
  const totalCents = payment.totalAmount
  const netCents = vatRate !== undefined && vatRate > 0 ? Math.round(totalCents / vatMultiplier) : totalCents
  const vatCents = vatRate !== undefined && vatRate > 0 ? totalCents - netCents : 0

  const ssn = enrollment.mainUser?.ssn
  const savedSsnInit = ssn || undefined

  const { showSsnInput, setShowSsnInput, savedSsn, isSavingSsn, ssnFormProps, handleSaveSsn } = useReceiptSsn(
    (ssnVal) =>
      addSsnForEnrollmentUser({
        clientId: enrollment.clientId,
        enrollmentId: enrollment.id,
        userType: 'mainUser',
        ssn: ssnVal
      })
  )

  const displaySsn = savedSsn ?? savedSsnInit

  const handleDownload = useCallback(() => {
    const ref = payment.paymentRef || enrollment.id
    downloadReceiptPdf(RECEIPT_ELEMENT_ID, `Receipt_${ref}.pdf`)
  }, [payment.paymentRef, enrollment.id])

  return (
    <DetailOverlay role="dialog" aria-modal="true" onClick={onClose}>
      <DetailCard onClick={(e) => e.stopPropagation()}>
        <DetailCardCloseButton onClick={onClose} aria-label={getText('portalClose')}>
          ×
        </DetailCardCloseButton>
        <DetailCardContent>
          <DetailCardTopRow>
            <DetailCardTitle>{getText('portalWalkinReceiptTitle')}</DetailCardTitle>
            <DetailCardActionsRow>
              {!isViewAs && (
                <DetailCardSsnToggle type="button" onClick={() => setShowSsnInput(!showSsnInput)}>
                  {displaySsn ? getText('portalWalkinEditSsn') : getText('portalWalkinAddSsn')}
                </DetailCardSsnToggle>
              )}
              <DetailCardDownloadButton onClick={handleDownload}>
                {getText('portalDownloadPdf')}
              </DetailCardDownloadButton>
            </DetailCardActionsRow>
          </DetailCardTopRow>

          {!isViewAs && showSsnInput && (
            <DetailCardSsnInputRow>
              <ReceiptSsnInputForm
                control={ssnFormProps.control}
                handleSubmit={ssnFormProps.handleSubmit}
                formState={ssnFormProps.formState}
                isSaving={isSavingSsn}
                onSave={handleSaveSsn}
              />
            </DetailCardSsnInputRow>
          )}

          <DetailCardReceiptBox id={RECEIPT_ELEMENT_ID}>
            <DetailCardReceiptRef>
              #{payment.swishPaymentReference || payment.paymentRef || enrollment.id}
            </DetailCardReceiptRef>

            {client && (client.name || client.orgNo || client.address) && (
              <DetailCardSellerBlock>
                <DetailCardSellerLabel>{getText('portalReceiptSeller')}</DetailCardSellerLabel>
                <DetailCardSellerValue>
                  {client.name && <span>{client.name}</span>}
                  {client.orgNo && <span>{client.orgNo}</span>}
                  {client.address && <span>{client.address}</span>}
                </DetailCardSellerValue>
              </DetailCardSellerBlock>
            )}

            <DetailCardReceiptRows>
              <DetailCardRow>
                <DetailCardRowLabel>{getText('componentsReceiptReceiptPaymentDateLabel')}</DetailCardRowLabel>
                <DetailCardRowValue>
                  {payment.paymentDate ? dayjs(payment.paymentDate).format('D MMM YYYY HH:mm') : '—'}
                </DetailCardRowValue>
              </DetailCardRow>
              <DetailCardRow>
                <DetailCardRowLabel>{getText('portalReceiptActivity')}</DetailCardRowLabel>
                <DetailCardRowValue>{activity.name}</DetailCardRowValue>
              </DetailCardRow>
              {quantity > 1 && (
                <DetailCardRow>
                  <DetailCardRowLabel>{getText('portalWalkinQuantity')}</DetailCardRowLabel>
                  <DetailCardRowValue>{quantity}</DetailCardRowValue>
                </DetailCardRow>
              )}

              {vatRate !== undefined && vatRate > 0 ? (
                <>
                  <DetailCardRow>
                    <DetailCardRowLabel>{getText('portalReceiptNetAmountExclVat')}</DetailCardRowLabel>
                    <DetailCardRowValue>{formatPrice(netCents, currency)}</DetailCardRowValue>
                  </DetailCardRow>
                  <DetailCardRow>
                    <DetailCardRowLabel>
                      {formatGrooverText(getText('componentsReceiptReceiptPaymentVat'), [String(vatRate)])}
                    </DetailCardRowLabel>
                    <DetailCardRowValue>{formatPrice(vatCents, currency)}</DetailCardRowValue>
                  </DetailCardRow>
                  <DetailCardRow>
                    <DetailCardRowLabel>{getText('portalReceiptTotalInclVat')}</DetailCardRowLabel>
                    <DetailCardRowValue>{formatPrice(totalCents, currency)}</DetailCardRowValue>
                  </DetailCardRow>
                </>
              ) : (
                <DetailCardRow>
                  <DetailCardRowLabel>{getText('portalReceiptAmount')}</DetailCardRowLabel>
                  <DetailCardRowValue>{formatPrice(totalCents, currency)}</DetailCardRowValue>
                </DetailCardRow>
              )}

              <DetailCardRow>
                <DetailCardRowLabel>{getText('portalReceiptPaymentMethod')}</DetailCardRowLabel>
                <DetailCardRowValue>Swish</DetailCardRowValue>
              </DetailCardRow>

              {displaySsn && (
                <DetailCardRow>
                  <DetailCardRowLabel>
                    <GrooverText textKey="receiptPersonalNumberLabel" />
                  </DetailCardRowLabel>
                  <DetailCardRowValue>{displaySsn}</DetailCardRowValue>
                </DetailCardRow>
              )}
            </DetailCardReceiptRows>

            {refundedCents > 0 && (
              <DetailCardRefundNote>
                {refundedCents >= totalCents
                  ? getText('portalWalkinFullyRefunded')
                  : `${getText('portalWalkinPartialRefundNote')} ${formatPrice(refundedCents, currency)}`}
              </DetailCardRefundNote>
            )}
          </DetailCardReceiptBox>

          <DetailCardCloseFooter onClick={onClose}>{getText('portalClose')}</DetailCardCloseFooter>
        </DetailCardContent>
      </DetailCard>
    </DetailOverlay>
  )
}
